import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ActivatedRoute,
  PRIMARY_OUTLET,
  Router,
  UrlSegment,
  UrlSegmentGroup,
  UrlTree,
} from '@angular/router';
import { LocalStorageService } from 'ngx-localstorage';
import { ValidationService } from '../services/validation.service';
import { SharedService } from './shared.service';
import { CustomValidators } from '../validators/customValidators';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  mainForm;
  currentForm;
  mainFormDefaults;
  fieldsForDelete = {
    postCodeX: [null, this.requiredIfExists()],
    addressX: [null, this.requiredIfExists()],
  };
  extraFields = {
    samePerson: [null, this.requiredIfExists()],
    isFinancedThroughLoan: [null, this.requiredIfExists()],
    isCompany: null,
    exchange: false,
    isPropertyAddressSameAsCorrespondence: [null, this.requiredIfExists()],
  };

  myHomeInfo = {
    claimsExists: [null, this.requiredIfExists()],
    // claimsExists: [null, this.requiredIfExists()],
    purpose: [null, this.requiredIfExists()],
    immovableinsuranceCheckbox: false,
    movableinsuranceCheckbox: false,
    errorControl: ['1', this.requiredIfExists()],
    packageType: null,
    exchange: null,
    numberOfInstallments: null,
    chosenPackageId: 0,
    agreement: this.formBuilder.group({
      question1: [null, this.requiredIfExists()],
      question2: [null, this.requiredIfExists()],
      question3: [null, this.requiredIfExists()],
      question4: [null, this.requiredIfExists()],
      agreement1: [null, [this.requiredIfExists(), this.requiredTrue()]],
      agreement2: [null, this.requiredIfExists()],
      agreement3: [null, this.requiredIfExists()],
    }),
    ownerData: this.formBuilder.group({
      email: [null, this.requiredIfExists()],
      countryCode: [null, this.requiredIfExists()],
      phoneNumber: [
        null,
        { validators: this.requiredIfExists(), updateOn: 'blur' },
      ],
      cityName: [null, this.requiredIfExists()],
      egn: ['', [this.requiredIfExists(), CustomValidators.personalId()]],
      firstName: [null, this.requiredIfExists()],
      companyName: [null, this.requiredIfExists()],
      middleName: [null, this.requiredIfExists()],
      lastName: [null, this.requiredIfExists()],
      postCode: [null, this.requiredIfExists()],
      address: [null, this.requiredIfExists()],
    }),
    policyholderData: this.formBuilder.group({
      email: [null, this.requiredIfExists()],
      countryCode: [null, this.requiredIfExists()],
      phoneNumber: [
        null,
        { validators: this.requiredIfExists(), updateOn: 'blur' },
      ],
      cityName: [null, this.requiredIfExists()],
      egn: ['', [this.requiredIfExists(), CustomValidators.personalId(true)]],
      firstName: [null, this.requiredIfExists()],
      middleName: [null, this.requiredIfExists()],
      lastName: [null, this.requiredIfExists()],
      postCode: [null, this.requiredIfExists()],
      address: [null, this.requiredIfExists()],
    }),
    saveOfferOwnerData: this.formBuilder.group({
      email: ['', { validators: Validators.email, updateOn: 'blur' }],
      firstName: null,
      lastName: null,
      countryCode: null,
      phoneNumber: null,
    }),
  };
  showGoogleAddress = false;
  constructor(
    private router: Router,
    public sharedService: SharedService,
    private storage: LocalStorageService,
    private route: ActivatedRoute,
    public validationService: ValidationService,
    private formBuilder: FormBuilder
  ) {}

  validateOwner(routeName: string): ValidatorFn {
    return (group: FormGroup): ValidationErrors => {
      const control1: any = group.controls['companyOwnerData'];
      const control2: any = group.controls['personOwnerData'];
      const insurances: any = group.controls['insurances'];
      const claimsExists10y: any = group.controls['claimsExists10y'];
      const tree: UrlTree = this.router.parseUrl(this.router.url);
      const g: UrlSegmentGroup = tree.root.children[PRIMARY_OUTLET];
      if (g) {
        const s: UrlSegment[] = g.segments;
        if (s[0].path !== routeName) {
          return;
        }
      }
      if (
        insurances.controls.mtplEndDate.value &&
        insurances.controls.cascoEndDate.value &&
        claimsExists10y.valid &&
        (control1.controls.postCode.value || control2.controls.postCode.value)
      ) {
        control2.setErrors(null);
      } else {
        control2.setErrors({ required: true });
      }
      return;
    };
  }

  requiredIfValidator(isFinancedThroughLoanF, isSameF): ValidatorFn {
    return (formControl) => {
      if (!formControl.parent) {
        return null;
      }
      if (!isSameF()) {
        return { required: true };
      }
      if (isFinancedThroughLoanF() === 'N') {
        return null;
      }
      if (!formControl.value) {
        return { required: true };
      }
      return null;
    };
  }

  requiredTrue() {
    return (formControl) => {
      if (formControl.value != true) {
        return { isFalse: true };
      }
      this.validationService.registerControl(formControl);
      return null;
    };
  }

  requiredIfExists() {
    return (formControl) => {
      if (!formControl.parent) {
        return null;
      }
      if (!formControl.nativeElement) {
        return null;
      }
      if (!document.body.contains(formControl.nativeElement)) {
        return null;
      }
      this.validationService.registerControl(formControl);
      return Validators.required(formControl);
    };
  }

  requiredForRoute(routeName: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const tree: UrlTree = this.router.parseUrl(this.router.url);
      const g: UrlSegmentGroup = tree.root.children[PRIMARY_OUTLET];
      if (g) {
        const s: UrlSegment[] = g.segments;
        if (s[0].path === routeName && !control.value) {
          if (control.errors && control.errors.nxDatefieldParse) {
            delete control.errors.nxDatefieldParse;
            return null;
          }
          return {
            required: { value: control.value },
          };
        }
      }
      return null;
    };
  }

  getMainForm() {
    if (!this.mainForm) {
      this.mainForm = this.formBuilder.group({
        propertyCategory: [null, this.requiredIfExists()],
        propertySubcategory: [null, this.requiredIfExists()],
        propertySubcategoryLabel: [null, this.requiredIfExists()],
        postCode: [null, this.requiredIfExists()],
        area: [null, this.requiredIfExists()],
        currency: null,
        doubleVisualization: null,
        cityName: [null, this.requiredIfExists()],
        cityId: null,
        township: '',
        district: 'Я',
        region: '',
        street: [null, this.requiredIfExists()],
        streetNumber: ['', this.requiredIfExists()],
        block: ['', this.requiredIfExists()],
        blockName: '',
        entrance: [null, this.requiredIfExists()],
        floor: [null, this.requiredIfExists()],
        apartment: [null, this.requiredIfExists()],
        fullAddress: ['', this.requiredIfExists()],
        longitude: '',
        latitude: '',
        placeId: '',
        numberOfFloors: '0',
        yearOfConstruction: [
          null,
          { validators: [this.requiredIfExists(), this.dateYearValidator()] },
        ],
        insuranceStart: [null, this.requiredIfExists()],
        chosenPremiumSaveOffer: null,
        chosenPremiumSaveOfferEuro:null,
        chosenPremiumSaveOfferCurr2:null,
        selectedPackageId: null,
        selectedSum: 154.64,
        selectedPayments: '1',
        immovableinsuranceSumRecommended: [null, this.requiredIfExists()],
        immovableinsuranceSumRecommendedCurr2: [null, this.requiredIfExists()],
        immovableinsuranceSum: "",
        movableinsuranceSumRecommended: [null, this.requiredIfExists()],
        movableinsuranceSumRecommendedCurr2: [null, this.requiredIfExists()],
        movableinsuranceSum: "",
        nErr_Code: 0,
        isRenewed: 'F',
        n_1NSP_Premium: null,
        n_2NSP_Premium: null,
        n_3NSP_Premium: null,
        n_1NSP_PremiumCurr2 : null,
        n_2NSP_PremiumCurr2 : null,
        n_3NSP_PremiumCurr2 : null,
        status: 'PREMIUMS_CALCULATED',
        contractProcessState: 'QUOTATION',
        insSumOutsideObjects: 0,
        insSumSpecificObjects: 0,
        isEarthQuakeIncluded: 'T',
        isGlassIncluded: 'F',
        isGoMaxIncluded: 'F',
        isDeductibleIncluded: 'F',
        commissionDiscount: 0,
        topComfort: 117.41,
        topExtra: 156.85,
        topMax: 196.51,
        bottomComfortOne: 117.41,
        bottomExtraOne: 156.85,
        bottomMaxOne: 196.51,
        bottomComfortTwo: 61.83,
        bottomExtraTwo: 82.85,
        bottomMaxTwo: 104.01,
        bottomComfortFour: 30.92,
        bottomExtraFour: 41.43,
        bottomMaxFour: 52.01,
        topComfortCurr2 : null,
        topExtraCurr2 : null,
        topMaxCurr2 :null,
        bottomComfortOneCurr2 : null,
        bottomExtraOneCurr2 : null,
        bottomMaxOneCurr2 : null,
        bottomComfortTwoCurr2 : null,
        bottomExtraTwoCurr2 : null,
        bottomMaxTwoCurr2 : null,
        bottomComfortFourCurr2 : null,
        bottomExtraFourCurr2 : null,
        bottomMaxFourCurr2 : null,
        glass: 5000,
        goLimit: 58500,
        selfParticipation: 0,
        emailCommunicationAgreement: 'F',
        insurerId: '1656571',
        insuredId: '1656571',
        payerId: '1656571',
        userId: '1656571',
        country: 'BG',
        cConfGenCond: 'T',
        usageId: '1',
        insuranceStartData: [null],
        companyBeneficiaryData: null,
        nsumm: null,
        nsummCurr2: null,
        sPlcNomer: null,
        nIDAgent:null,
        nIdAgentNumber: null,
        firstCountryCode: [null, this.requiredIfExists()],
        firstPhoneNumber: [
          null,
          { validators: this.requiredIfExists(), updateOn: 'blur' },
        ],
        firstFirstName: [null, this.requiredIfExists()],
        ...this.myHomeInfo,
        ...this.extraFields,
      });

      this.mainFormDefaults = this.mainForm.getRawValue();
      this.currentForm = this.mainForm;
     
    }
    this.currentForm.get('ownerData.egn').valueChanges.subscribe((value) => {
      if (this.currentForm.get('ownerData.egn').valid) {
        this.sharedService.newRegSubj.next(value);
      } else {
        this.sharedService.newRegSubj.next('INVALID');
      }
    });
    return this.mainForm;
  }

  dateValidator(): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } | null => {
      const expr = new RegExp('^d{2}(?:[-/,.]?){1}d{2}(?:[-/,.]?){1}d{4}');
      let match = expr.test(c.value);
      if (c.pristine) {
        return null;
      }
      if (!match) {
        return { dateInvalid: true };
      }
      if (c.value !== undefined && c.value !== '' && c.value != null) {
        var month = null;
        var day = null;
        var year = null;
        var currentTaxYear = Number(new Date().getFullYear() - 1);
        if (c.value.indexOf('/') > -1) {
          var res = c.value.split('/');
          if (res.length > 1) {
            month = res[0];
            day = res[1];
            year = res[2];
          }
        } else {
          if (c.value.length == 8) {
            month = c.value.substring(0, 2);
            day = c.value.substring(2, 4);
            year = c.value.substring(4, 8);
          }
        }
        if (isNaN(month) || isNaN(day) || isNaN(year)) {
          return { dateInvalid: true };
        }
        month = Number(month);
        day = Number(day);
        year = Number(year);
        if (month < 1 || month > 12) {
          // check month range
          return { dateInvalid: true };
        }
        if (day < 1 || day > 31) {
          return { dateInvalid: true };
        }
        if (
          (month === 4 || month === 6 || month === 9 || month === 11) &&
          day === 31
        ) {
          return { dateInvalid: true };
        }
        if (month == 2) {
          // check for february 29th
          var isleap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
          if (day > 29 || (day === 29 && !isleap)) {
            return { dateInvalid: true };
          }
        }
        if (year !== currentTaxYear) {
          return { dateYearGreaterThanTaxYear: true };
        }
      }
      return null;
    };
  }

  dateYearValidator(): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } | null => {
      const expr = new RegExp('d{4}');
      let match = expr.test(c.value);
      if (c.pristine) {
        return null;
      }
      if (!match) {
        return { dateInvalid: true };
      }
      return null;
    };
  }
  reset() {
    if (this.mainFormDefaults) {
      this.mainForm.reset(this.mainFormDefaults);
    }

    this.storage.remove('data');
    this.storage.remove('firstInstallment');
    this.storage.remove('dueId');
  }
}
